package screens.post

import androidx.compose.runtime.*
import com.example.readingnotes.di.AppModule
import com.example.readingnotes.presentation.postdetail.PostDetailEvent
import com.example.readingnotes.presentation.postdetail.PostDetailViewModel
import com.example.readingnotes.util.DateFormatter
import kotlinx.coroutines.CoroutineScope
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import styles.AppStyleSheet
import utils.SseClient
import utils.sanitizeHtml

@Composable
fun PostDetailScreen(
    postId: Int,
    onBackClick: () -> Unit,
    isLoggedIn: Boolean = false,
    onEditClick: (Int) -> Unit = {},
    appModule: AppModule,
    appScope: CoroutineScope,
) {
    val viewModel =
        remember {
            PostDetailViewModel(
                loadPostDetailUseCase = appModule.loadPostDetailUseCase,
                loadCommentsUseCase = appModule.loadCommentsUseCase,
                createCommentUseCase = appModule.createCommentUseCase,
                viewModelScope = appScope,
            )
        }
    val state by viewModel.state.collectAsState()

    // SSE client for real-time comment updates
    val apiBaseUrl = js("window.API_BASE_URL || 'http://localhost:3001'") as String
    val sseClient = remember { SseClient.create(apiBaseUrl) }

    LaunchedEffect(postId) {
        viewModel.onEvent(PostDetailEvent.OnAppear(postId))

        // Set up SSE event listeners for comments
        sseClient.addEventListener("comment.created") { event ->
            console.log("SSE: Comment created event received", event.data)
            // Reload comments to get the latest list
            viewModel.onEvent(PostDetailEvent.OnLoadComments(postId))
        }

        sseClient.addEventListener("comment.deleted") { event ->
            console.log("SSE: Comment deleted event received", event.data)
            // Reload comments to get the latest list
            viewModel.onEvent(PostDetailEvent.OnLoadComments(postId))
        }

        // Connect to SSE
        sseClient.connect()
    }

    // Cleanup SSE connection on dispose
    DisposableEffect(Unit) {
        onDispose {
            sseClient.disconnect()
        }
    }

    Div({
        classes(AppStyleSheet.detailContainer)
        attr("data-testid", "post-detail")
    }) {
        // Header with back button and edit button
        Div({ classes(AppStyleSheet.detailHeader) }) {
            Button({
                classes(AppStyleSheet.backButton)
                onClick { onBackClick() }
            }) { Text("← 戻る") }

            // Show edit button only for logged-in admins
            if (isLoggedIn) {
                Button({
                    classes(AppStyleSheet.editButton)
                    onClick { onEditClick(postId) }
                }) { Text("編集") }
            }
        }

        when {
            state.isLoading -> {
                Div({ classes(AppStyleSheet.loading) }) { Text("読み込み中...") }
            }
            state.errorMessage != null -> {
                Div({ classes(AppStyleSheet.error) }) { Text(state.errorMessage!!) }
            }
            state.post != null -> {
                // Safe to use !! because we already checked != null above
                val post = state.post!!
                // Post detail section
                Div({ classes(AppStyleSheet.postDetail) }) {
                    H1({
                        classes(AppStyleSheet.postTitle)
                        attr("data-testid", "post-title")
                    }) { Text(post.title) }
                    Div({ classes(AppStyleSheet.postMeta) }) {
                        Text("投稿日: ${DateFormatter.formatDateTime(post.createdAt)}")
                        val author = post.author
                        if (author != null) {
                            Text(" | 投稿者: ${author.username ?: "匿名"}")
                        }
                    }
                    Div({
                        classes(AppStyleSheet.postBody)
                    }) {
                        // Use safe HTML rendering with DOMPurify to prevent XSS attacks
                        Div({
                            classes(AppStyleSheet.postBody)
                            attr("innerHTML", post.contentHtml ?: sanitizeHtml(post.contentMarkdown.replace("\n", "<br>")))
                            attr("data-testid", "post-content")
                        }) { }
                    }
                }

                // Comments section
                Div({ classes(AppStyleSheet.commentsSection) }) {
                    H2 { Text("コメント (${state.comments.size})") }

                    // Comment list
                    if (state.comments.isEmpty()) {
                        P({ classes(AppStyleSheet.noComments) }) { Text("まだコメントはありません") }
                    } else {
                        Div({ classes(AppStyleSheet.commentList) }) {
                            state.comments.forEach { comment ->
                                Div({ classes(AppStyleSheet.commentCard) }) {
                                    Div({ classes(AppStyleSheet.commentHeader) }) {
                                        Span({ classes(AppStyleSheet.commentAuthor) }) { Text(comment.authorName) }
                                        Span({ classes(AppStyleSheet.commentDate) }) {
                                            Text(DateFormatter.formatDateTime(comment.createdAt))
                                        }
                                    }
                                    P({ classes(AppStyleSheet.commentContent) }) { Text(comment.content) }
                                }
                            }
                        }
                    }

                    // Comment form
                    Div({ classes(AppStyleSheet.commentForm) }) {
                        H3 { Text("コメントを投稿") }

                        // Author name input
                        Div({ classes(AppStyleSheet.formGroup) }) {
                            Label { Text("名前（任意）") }
                            Input(type = InputType.Text) {
                                classes(AppStyleSheet.textInput)
                                value(state.authorNameInput)
                                onInput { viewModel.onEvent(PostDetailEvent.OnAuthorNameChanged(it.value)) }
                            }
                        }

                        // Comment content input
                        Div({ classes(AppStyleSheet.formGroup) }) {
                            Label { Text("コメント") }
                            TextArea(attrs = {
                                classes(AppStyleSheet.textArea)
                                attr("placeholder", "コメントを入力してください...")
                                attr("rows", "4")
                                attr("maxlength", "1000")
                                onInput { viewModel.onEvent(PostDetailEvent.OnCommentContentChanged(it.value)) }
                            }, value = state.commentInput)
                        }

                        // Error message
                        if (state.commentErrorMessage != null) {
                            Div({ classes(AppStyleSheet.formError) }) { Text(state.commentErrorMessage!!) }
                        }

                        // Submit button
                        Button({
                            classes(AppStyleSheet.submitButton)
                            onClick { viewModel.onEvent(PostDetailEvent.OnCommentSubmit) }
                            if (state.isSubmittingComment || state.commentInput.isBlank()) {
                                attr("disabled", "true")
                            }
                            style {
                                if (state.commentInput.isBlank()) {
                                    property("opacity", "0.6")
                                    property("cursor", "not-allowed")
                                }
                            }
                        }) {
                            Text(if (state.isSubmittingComment) "送信中..." else "コメントを送信 (${state.commentInput.length}/1000)")
                        }
                    }
                }
            }
        }
    }
}
